import React from 'react'
// components
import Chip from '@mui/material/Chip'
import TableRow from '@mui/material/TableRow'
import { Table } from 'components/UI'
// context
import { ApiClientsPageContext } from 'containers/ApiClients/layers/API'
// types
import { IApiClient } from 'types/graphql'
// themes
import { TableCell } from 'themes/views/tables'
// view components
import ControlButtons from './components/ControlButtons'
// views
import {
  IpList,
  MonoCell,
  NoWrap,
  ScopeChip,
  ScopeChipActions,
  ScopeChipEntity,
  ScopeChips,
  StickyActions
} from '../../views'
// themes
import { colors } from 'themes/index'
// utils
import moment from 'moment'
import { groupScopesByEntity, maskSecretPreview } from 'utils/apiClient'

const headers = [
  { id: 'name', sortable: false, left: true, label: 'NAME' },
  { id: 'expiresAt', sortable: false, left: true, label: 'EXPIRES AT' },
  { id: 'scopes', sortable: false, left: true, label: 'SCOPES' },
  { id: 'active', sortable: false, left: true, label: 'ACTIVE' },
  { id: 'revokedAt', sortable: false, left: true, label: 'REVOKED AT' },
  { id: 'clientId', sortable: false, left: true, label: 'CLIENT ID' },
  { id: 'secretPreview', sortable: false, left: true, label: 'SECRET PREVIEW' },
  { id: 'ip', sortable: false, left: true, label: 'IP' },
  { id: 'actions', sortable: false, left: true, label: '' }
]

const formatDate = (value?: string | null) => (value ? moment(value).format('DD/MM/YYYY [at] h:mma') : '—')

const ApiClientsTable: React.FC = () => {
  const { data, page, perPage, totalCount, isRefetching, refetch, handleSetPage, handleSetPerPage } =
    React.useContext(ApiClientsPageContext)

  return (
    <StickyActions>
      <Table<IApiClient>
        data={data}
        page={page}
        title='API Clients'
        perPage={perPage}
        refetch={refetch}
        headers={headers}
        totalCount={totalCount}
        isLoading={isRefetching}
        onPageChange={handleSetPage}
        onPerPageChange={handleSetPerPage}>
        {({ data }) =>
          data.map((client) => {
            const { id, name, scopes, active, revokedAt, clientId, secretPreview, secretExpiresAt, allowedIpRanges } =
              client
            const isRevoked = !!revokedAt
            const scopeGroups = groupScopesByEntity(scopes || [])

            return (
              <TableRow key={id} hover data-testid='api-client-row' data-entity-id={id}>
                <TableCell size={18}>
                  <NoWrap>{name || '—'}</NoWrap>
                </TableCell>
                <TableCell size={15}>
                  <NoWrap>{formatDate(secretExpiresAt)}</NoWrap>
                </TableCell>
                <TableCell size={22}>
                  <ScopeChips>
                    {scopeGroups.map(({ entity, label, actions }) => (
                      <ScopeChip
                        key={entity}
                        size='small'
                        label={
                          <>
                            <ScopeChipEntity>{label}:</ScopeChipEntity>{' '}
                            <ScopeChipActions>{actions.join(' · ')}</ScopeChipActions>
                          </>
                        }
                      />
                    ))}
                    {!scopeGroups.length && '—'}
                  </ScopeChips>
                </TableCell>
                <TableCell size={8}>
                  <Chip
                    size='small'
                    label={active ? 'Active' : 'Inactive'}
                    sx={{ bgcolor: active ? colors.green400 : colors.red250, color: colors.white100 }}
                  />
                </TableCell>
                <TableCell size={13}>
                  <NoWrap>{formatDate(revokedAt)}</NoWrap>
                </TableCell>
                <TableCell size={16}>
                  <MonoCell>{clientId || '—'}</MonoCell>
                </TableCell>
                <TableCell size={12}>
                  <MonoCell>{maskSecretPreview(secretPreview)}</MonoCell>
                </TableCell>
                <TableCell size={14}>
                  {allowedIpRanges?.length ? (
                    <IpList>
                      {allowedIpRanges.map((range) => (
                        <MonoCell key={range}>{range}</MonoCell>
                      ))}
                    </IpList>
                  ) : (
                    <MonoCell>—</MonoCell>
                  )}
                </TableCell>
                <ControlButtons client={client} disabled={isRevoked} />
              </TableRow>
            )
          })
        }
      </Table>
    </StickyActions>
  )
}

export default ApiClientsTable
